import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-10 w-10 rounded-lg bg-#5B6B8F flex items-center justify-center">
                <Lightbulb className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-zinc-900">GBA Idea App</span>
            </div>
            <h1 className="text-4xl font-bold text-zinc-900 mb-2">Welcome back</h1>
            <p className="text-lg text-zinc-600">Sign in to continue capturing ideas</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-zinc-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="flex h-11 w-full rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-#E8F5E90 focus-visible:border-transparent transition-all"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-zinc-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="flex h-11 w-full rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-#E8F5E90 focus-visible:border-transparent transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-#5B6B8F text-white hover:bg-#4A5A7C shadow-sm rounded-lg font-medium transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-zinc-600">
            Don't have an account?{' '}
            <Link to="/signup" className="text-#5B6B8F hover:text-#4A5A7C font-medium" data-testid="signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Hero */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-#E8F5E9 to-amber-50 items-center justify-center p-12">
        <div className="max-w-lg">
          <h2 className="text-4xl font-bold text-zinc-900 mb-4">Your ideas deserve a home</h2>
          <p className="text-lg text-zinc-600 mb-8">
            Capture, organize, and bring your ideas to life with team collaboration and progress tracking.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-#5B6B8F flex items-center justify-center text-white font-mono text-sm">1</div>
              <div>
                <h3 className="font-semibold text-zinc-900">Capture instantly</h3>
                <p className="text-sm text-zinc-600">Log ideas as they come with categories and tags</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-#5B6B8F flex items-center justify-center text-white font-mono text-sm">2</div>
              <div>
                <h3 className="font-semibold text-zinc-900">Track progress</h3>
                <p className="text-sm text-zinc-600">Monitor idea development with status tracking</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-#5B6B8F flex items-center justify-center text-white font-mono text-sm">3</div>
              <div>
                <h3 className="font-semibold text-zinc-900">Collaborate</h3>
                <p className="text-sm text-zinc-600">Assign ideas to team members and track accountability</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;